import React, { useEffect, useRef } from "react";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { motion, useAnimation, useInView, Variants } from "framer-motion";

const fadeUpVariant: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ScrollFadeBlock: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.2 });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      variants={fadeUpVariant}
      initial="hidden"
      animate={controls}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const ProposalSection: React.FC = () => {
  return (
    <section id="propuesta" className="section-hyper scroll-snap-section">

      <ScrollFadeBlock className="section-title-hyper">
        ¿Querés saber qué hace una administradora de autos como la nuestra?
      </ScrollFadeBlock>

      <ScrollFadeBlock className="intro-text">
        <p>Nos encargamos de todo para que tu auto genere plata sin que tengas que mover un dedo.</p>          
        <p>Desde encontrar al conductor hasta controlar su uso, mantener el auto en condiciones y garantizar el pago mensual.</p>
        <p>En Arrank!, vos invertís y nosotros trabajamos por vos.</p>
      </ScrollFadeBlock>

      <ScrollFadeBlock className="dual-proposal-grid">
        <ScrollFadeBlock className="proposal-card proposal-owners parallax-element">
          <h1 className="proposal-title">Para dueños de autos</h1>
          <p className="proposal-description">
            Poné tu auto a trabajar de forma segura y rentable. 
            Nos encargamos de la selección de conductores, contratos, seguros y gestión mensual.
            Vos disfrutás los ingresos sin complicaciones.
          </p>
          <div className="proposal-features">
            <span className="feature-tag"><i className="bi bi-cash-coin me-3 staff-icon-animado" style={{ fontSize: '3rem' }}></i> Ingresos mensuales asegurados</span>
            <span className="feature-tag"><i className="bi bi-gear me-3 staff-icon-animado" style={{ fontSize: '3rem' }}></i> Gestión 100% automatizada</span>
            <span className="feature-tag"><i className="bi bi-shield-lock me-3 staff-icon-animado" style={{ fontSize: '3rem' }}></i> Seguro integral incluido</span>
          </div>
        </ScrollFadeBlock>
        
        <ScrollFadeBlock className="proposal-card proposal-drivers parallax-element">
          <h3 className="proposal-title">Para conductores</h3>
          <p className="proposal-description">
            Accedé fácil y rápido a un auto para trabajar.
            Nos ocupamos de todo: selección, contratos, seguros y gestión mensual.
            Solo enfocáte en manejar y generar ingresos.
          </p>
          <div className="proposal-features">
            <span className="feature-tag"><i className="bi bi-file-earmark-check me-3 staff-icon-animado" style={{ fontSize: '3rem' }}></i> Proceso de alta rápido</span>
            <span className="feature-tag"><i className="bi bi-person-rolodex me-3 staff-icon-animado" style={{ fontSize: '3rem' }}></i> Monotributo incluido</span>
            <span className="feature-tag"><i className="bi bi-headset me-3 staff-icon-animado" style={{ fontSize: '3rem' }}></i> Soporte 24/7</span>
          </div>
        </ScrollFadeBlock>
      </ScrollFadeBlock>

      <ScrollFadeBlock className="staff-section">
        <h3 className="staff-title">Nuestro Staff Profesional</h3>
        <div className="staff-content">
          <p className="staff-intro">
            Nuestro equipo cuenta con una sólida y diversa trayectoria profesional que respalda cada aspecto de nuestra gestión.
          </p>
          <div className="staff-expertise">
            <div className="expertise-item">
              <span className="expertise-icon staff-icon-animado"><i className="bi bi-car-front"></i></span>
              <span>Sector Automotriz</span>
            </div>
            <div className="expertise-item">
              <span className="expertise-icon staff-icon-animado"><i className="bi bi-bar-chart"></i></span>
              <span>Administración</span>
            </div>
            <div className="expertise-item">
              <span className="expertise-icon staff-icon-animado"><i className="bi bi-folder-check"></i></span>
              <span>Operaciones</span>
            </div>
            <div className="expertise-item">
              <span className="expertise-icon staff-icon-animado"><i className="bi bi-briefcase"></i></span>
              <span>Contabilidad</span>
            </div>
            <div className="expertise-item">
              <span className="expertise-icon staff-icon-animado"><i className="bi bi-person-fill-check"></i></span>
              <span>Recursos Humanos</span>
            </div>
          </div>
          <p className="staff-focus">
            El foco de trabajo está puesto en nuestros clientes y socios, y para ello el bienestar de nuestros colaboradores es clave.
          </p>
        </div>
      </ScrollFadeBlock>

      <hr className="section-divider" />
    </section>
  );
};

export default ProposalSection;
